// SPDX-FileCopyrightText: © 2025 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  Watch,
} from '@stencil/core'
import type { LiteralUnion } from 'type-fest'

import type { QdsFocusEventDetail } from '../../helpers'
import { pickFocusEventAttributes } from '../../helpers'
import type { ButtonTarget } from '../button/button'

const preventDefault = (event: KeyboardEvent | MouseEvent): void => {
  event.preventDefault()
}

/**
 * @experimental
 */
@Component({
  tag: 'qds-mini-button',
  formAssociated: true,
  styleUrl: 'mini-button.css',
  shadow: true,
})
export class MiniButton implements ComponentInterface {
  /**
   * Prevents the mini button from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop()
  public readonly disabled?: boolean

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly name!: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly library: string = 'default'

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<ButtonTarget, string>

  /**
   * When set, the underlying button will be rendered as an `<a>` with this
   * `href` instead of a `<button>`.
   *
   * @webnative
   */
  @Prop() public readonly href?: string

  /**
   * When href is define and the button act as a link (`<a>`), the `rel` will be added to the element.
   *
   * @webnative
   */
  @Prop() public readonly rel?: string = 'noopener'

  /**
   * The mini button's accessible name.
   */
  @Prop() public readonly text!: string

  /**
   * Emitted when the button loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the button gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @AttachInternals() private readonly internals!: ElementInternals

  #savedTabIndex = 0

  get #computedDisabled(): boolean {
    return (
      this.host.matches(':disabled') ||
      (this.host.hasAttribute('disabled') &&
        this.host.getAttribute('disabled') !== 'false')
    )
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.internals.ariaDisabled = this.#computedDisabled.toString()
    if (this.#computedDisabled) this.host.removeAttribute('tabindex')
    else this.host.tabIndex = this.#savedTabIndex
  }

  @Watch('href')
  protected hrefChanged(): void {
    this.internals.role = this.href === undefined ? 'button' : 'link'
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string | null): void {
    if (newValue !== null) {
      const parsedValue = Number.parseInt(newValue, 10)
      this.#savedTabIndex =
        Number.isNaN(parsedValue) || !Number.isFinite(parsedValue)
          ? 0
          : parsedValue
    }
    if (this.#computedDisabled) this.host.removeAttribute('tabindex')
    else this.host.tabIndex = this.#savedTabIndex
  }

  @Watch('text')
  protected textChanged(): void {
    this.internals.ariaLabel = this.text
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
    }
  }

  @Listen('blur')
  protected onBlur(event: FocusEvent): void {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  @Listen('focus')
  protected onFocus(event: FocusEvent): void {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  @Listen('keydown')
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') this.host.click()
  }

  @Listen('keyup')
  protected onKeyup(event: KeyboardEvent): void {
    if (event.key === ' ') this.host.click()
  }

  public componentWillLoad(): void {
    this.hrefChanged()
    this.textChanged()
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
    this.disabledChanged()
  }

  public render() {
    const Tag = this.href === undefined ? 'button' : 'a'

    return (
      <Tag
        type={Tag === 'a' ? undefined : 'button'}
        class="qds-mini-button"
        href={Tag === 'a' ? this.href : undefined}
        rel={Tag === 'a' ? this.rel : undefined}
        disabled={this.#computedDisabled}
        aria-hidden="true"
        onKeyDown={preventDefault}
        onKeyUp={preventDefault}
        onKeyPress={preventDefault}
        tabIndex={-1}
      >
        <qds-icon class="qds-icon" library={this.library} name={this.name} />
      </Tag>
    )
  }
}
